import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import styled, { css } from 'styled-components'
import { match } from 'ts-pattern'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Redacted } from '@blade/std-lib'
import { reatomRegistration } from '@root/features/auth/authenticate'
import { useTranslation } from '@root/shared/localization'
import { AuthModal } from '@root/shared/ui'
import { mediaMaxMobile, ProfileIcon } from '@root/shared/ui-kit'

import { AuthRegisterMain } from './auth-register-main'
import { AuthRegisterUsernameInfo } from './auth-register-username-info'

import type { InviteCode } from '@root/entities/referral'
import type {
  RegistrationMethod,
  RegistrationPayload,
} from '@root/features/auth/authenticate'
import type { Intent } from '@root/shared/ui-kit'

export type AuthFormValues = {
  username: string
  password: string
  repeatPassword: string
  referralCode: InviteCode
}

type AuthRegisterProps = {
  registrationMethod: RegistrationMethod
}

export const MIN_USERNAME_LENGTH = 3

// TODO: Ivan - add validation states and info messages
export const AuthRegister: React.FC<AuthRegisterProps> = ({
  registrationMethod,
}) => {
  const { t } = useTranslation()
  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomRegistration(), [])

  const [registerRequestState] = useAtom(model.registerStateAtom)
  const register = useAction(model.registerAction)

  const [checkIsUsernameAvailableState] = useAtom(
    model.checkIsUsernameAvailableStateAtom,
  )
  const checkIsUsernameAvailable = useAction(
    model.checkIsUsernameAvailableAction,
  )

  const { control, formState, handleSubmit, watch } = useForm<AuthFormValues>({
    defaultValues: {
      username: '',
      referralCode: registrationMethod.referralCode,
      password: '',
      repeatPassword: '',
    },
  })

  const username = watch('username')

  useEffect(() => {
    checkIsUsernameAvailable(username)
  }, [username, checkIsUsernameAvailable])

  const formError = useMemo(() => {
    if (
      AsyncStateN.isSuccess(checkIsUsernameAvailableState) &&
      !checkIsUsernameAvailableState.value
    ) {
      return t('auth.username-not-available-error')
    }

    if (formState.errors.username) {
      return formState.errors.username.message
    }

    if (formState.errors.repeatPassword) {
      return formState.errors.repeatPassword.message
    }
  }, [
    formState.errors.username,
    formState.errors.repeatPassword,

    checkIsUsernameAvailableState,
    t,
  ])

  const usernameIntent = useMemo(() => {
    const username = watch('username')
    const intent: Intent = AsyncStateN.matchWaiting(
      checkIsUsernameAvailableState,
      {
        Success: (value) =>
          (value === true ? 'success' : 'error') satisfies Intent,
        Error: () => 'error' satisfies Intent,
        Waiting: () => 'neutral' satisfies Intent,
      },
    )

    return username.trim().length >= MIN_USERNAME_LENGTH ? intent : 'neutral'
  }, [watch, checkIsUsernameAvailableState])

  const handleSubmitInternal = handleSubmit(async (values) => {
    const common = {
      password: Redacted.make(values.password),
      username: values.username,
      referralCode: values.referralCode || undefined,
    }
    const payload = match(registrationMethod)
      .returnType<RegistrationPayload>()
      .with({ type: 'telegram' }, ({ code, token }) => ({
        ...common,
        type: 'telegram',
        code,
        token,
      }))
      .with({ type: 'google' }, ({ code }) => ({
        ...common,
        type: 'google',
        code,
      }))
      .with({ type: 'twitter' }, ({ code }) => ({
        ...common,
        type: 'twitter',
        code,
      }))
      .with({ type: 'dev-username' }, () => ({
        ...common,
        type: 'dev-username',
      }))
      .exhaustive()

    register(payload)
  })

  return (
    <AuthModal
      infoIcon={ProfileIcon}
      infoMessage={
        <AuthRegisterUsernameInfo
          username={username}
          usernameIntent={usernameIntent}
        />
      }
    >
      <Form onSubmit={handleSubmitInternal}>
        <AuthRegisterMain
          usernameIntent={usernameIntent}
          control={control}
          formError={formError}
          registerRequestState={registerRequestState}
          checkIsUsernameAvailableState={checkIsUsernameAvailableState}
        />
      </Form>
    </AuthModal>
  )
}
const Form = styled.form`
  display: grid;
  max-width: 320px;
  ${mediaMaxMobile(css`
    max-width: none;
  `)}
`
